/**
 * Live push client for `GET /v1/events` (SSE). Framework-free so it can be
 * tested with a fake EventSource. The React side (LiveProvider) turns
 * events into TanStack Query invalidations.
 *
 * - Reconnects with capped backoff; EventSource's own retry is replaced so
 *   a 401/403 does not hammer the server.
 * - The office selector value travels as `?office=`, since EventSource
 *   cannot set headers; the server re-resolves it against the user's access.
 * - `resync` (the server could not replay from Last-Event-ID) and every
 *   reconnect after a drop mean "you may have missed changes": the
 *   provider invalidates everything live-backed.
 */
import {
  EntityChangedSchema,
  EVENTS_PATH,
  OFFICE_QUERY_PARAM,
  SSE_EVENTS,
  type EntityChanged,
} from '@velora/contracts';

export type LiveStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface EventSourceLike {
  readonly readyState: number;
  onopen: ((ev: Event) => unknown) | null;
  onerror: ((ev: Event) => unknown) | null;
  addEventListener(type: string, listener: (ev: MessageEvent) => void): void;
  close(): void;
}

export type EventSourceFactory = (url: string) => EventSourceLike;

export interface LiveClientOptions {
  /** Origin-relative base; default '' so the URL is `/v1/events`. */
  baseUrl?: string;
  createEventSource?: EventSourceFactory;
  onChange: (event: EntityChanged) => void;
  /** Called after a reconnect or a server `resync`: refetch everything live. */
  onResync: () => void;
  onStatus?: (status: LiveStatus) => void;
  /** Backoff in ms for attempt n (0-based). */
  backoff?: (attempt: number) => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

export const defaultBackoff = (attempt: number): number =>
  Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5)) + Math.floor(Math.random() * 500);

export class LiveClient {
  private source: EventSourceLike | null = null;
  private office: string | null = null;
  private attempt = 0;
  private timer: unknown = null;
  private everOpened = false;
  private statusValue: LiveStatus = 'idle';
  private readonly opts: Required<Omit<LiveClientOptions, 'onStatus'>> &
    Pick<LiveClientOptions, 'onStatus'>;

  constructor(options: LiveClientOptions) {
    this.opts = {
      baseUrl: '',
      createEventSource: (url) =>
        new EventSource(url, { withCredentials: true }) as unknown as EventSourceLike,
      backoff: defaultBackoff,
      setTimer: (fn, ms) => setTimeout(fn, ms),
      clearTimer: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
      ...options,
    };
  }

  get status(): LiveStatus {
    return this.statusValue;
  }

  /** Connects (or reconnects) for an office selector value. */
  connect(office: string): void {
    if (this.office === office && this.source) return;
    this.office = office;
    this.attempt = 0;
    this.everOpened = false;
    this.open();
  }

  close(): void {
    this.office = null;
    this.teardown();
    this.setStatus('closed');
  }

  url(office: string): string {
    const q = new URLSearchParams({ [OFFICE_QUERY_PARAM]: office });
    return `${this.opts.baseUrl}${EVENTS_PATH}?${q.toString()}`;
  }

  private open(): void {
    this.teardown();
    if (this.office === null) return;
    this.setStatus(this.everOpened ? 'reconnecting' : 'connecting');
    const es = this.opts.createEventSource(this.url(this.office));
    this.source = es;

    es.onopen = () => {
      if (this.source !== es) return;
      const wasReconnect = this.everOpened;
      this.everOpened = true;
      this.attempt = 0;
      this.setStatus('open');
      if (wasReconnect) this.opts.onResync();
    };
    es.onerror = () => {
      if (this.source !== es) return;
      // Take over retrying so the backoff is ours.
      es.close();
      this.source = null;
      this.scheduleReconnect();
    };
    es.addEventListener(SSE_EVENTS.entityChanged, (ev) => {
      if (this.source !== es) return;
      const parsed = EntityChangedSchema.safeParse(safeJson(ev.data));
      if (parsed.success) this.opts.onChange(parsed.data);
    });
    es.addEventListener(SSE_EVENTS.resync, () => {
      if (this.source !== es) return;
      this.opts.onResync();
    });
  }

  private scheduleReconnect(): void {
    if (this.office === null) return;
    this.setStatus('reconnecting');
    const delay = this.opts.backoff(this.attempt);
    this.attempt += 1;
    this.timer = this.opts.setTimer(() => {
      this.timer = null;
      this.open();
    }, delay);
  }

  private teardown(): void {
    if (this.timer !== null) {
      this.opts.clearTimer(this.timer);
      this.timer = null;
    }
    if (this.source) {
      this.source.close();
      this.source = null;
    }
  }

  private setStatus(s: LiveStatus): void {
    if (s === this.statusValue) return;
    this.statusValue = s;
    this.opts.onStatus?.(s);
  }
}

function safeJson(data: unknown): unknown {
  if (typeof data !== 'string') return data;
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

/**
 * Query-key convention for live data: a query whose key starts with an
 * entity key (`['appointment', ...]`) is refetched when that entity
 * changes. A key may also list extra entity keys in a `{ live: [...] }`
 * object anywhere in the key (for screens that join several entities).
 */
export function queryKeyMatchesEntity(queryKey: readonly unknown[], entityKey: string): boolean {
  const head = queryKey[0];
  if (head === entityKey) return true;
  if (typeof head === 'string' && entityKey.startsWith(`${head}.`)) return true;
  for (const part of queryKey) {
    if (part && typeof part === 'object' && 'live' in part) {
      const live = (part as { live: unknown }).live;
      if (Array.isArray(live) && live.includes(entityKey)) return true;
    }
  }
  return false;
}
